import { invariant } from '@epic-web/invariant'
import { TreeManager } from '@basehub/utils/tree/index'
import { getBlockDisplayData } from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import {
  AtLeast,
  Block,
  CommonBlocksCompoundIndexes,
  ReferenceBlock,
  RichTextBlock,
  isComponentBlock,
  isInstanceBlock,
  isReferenceableBlock,
} from '@basehub/data/replicache/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'

export type TypeOption = {
  id: Block['id']
  label: string
  type: Block['type']
  preview?: { url: string; mimeType: string; width: number; height: number }
}

export const getAllPossibleTypeOptions = (
  blockTreeManager:
    | TreeManager<Block, CommonBlocksCompoundIndexes>
    | {
        getNode: TreeManager<Block, CommonBlocksCompoundIndexes>['getNode']
        componentsSet: Set<string>
        getChildrenKeys: TreeManager<
          Block,
          CommonBlocksCompoundIndexes
        >['getChildrenKeys']
      },
  block?: Block
) => {
  const options: TypeOption[] = []

  const componentsSet =
    'compoundIndexes' in blockTreeManager
      ? ((blockTreeManager.compoundIndexes.get('components') ??
          new Set()) as Set<string>)
      : blockTreeManager.componentsSet

  for (const cId of componentsSet) {
    const node = blockTreeManager.getNode(cId, { required: true })
    invariant(isComponentBlock(node.value), 'node should be a component')
    let _option: TypeOption | undefined = undefined

    const sourceBlockId = idToSourceBlockId(node.value)
    if (options.some((o) => o.id === sourceBlockId)) {
      // prevent duplicates
      continue
    }
    const parentBlock = node.parentKey
      ? blockTreeManager.getNode(node.parentKey)?.value
      : null

    if (
      parentBlock &&
      (parentBlock.type === 'reference' || parentBlock.type === 'rich-text') &&
      blockTreeManager
        .getChildrenKeys(idToSourceBlockId(parentBlock))
        .some((childId) => childId === node.value.id)
    ) {
      // is internal block
      if (
        block &&
        (block.id === parentBlock.id ||
          (block.type === 'reference' &&
            block.constraints.acceptedTypes?.includes(
              idToSourceBlockId(node.value)
            )))
      ) {
        // internal to this ref, or is already being referenced, cool!
      } else {
        continue
      }
    }

    _option = {
      id: sourceBlockId,
      label: getBlockDisplayData({
        block: node.value,
        parentBlock,
      }).displayTitle,
      type: node.value.type,
      preview: node.value.typeOptions.displayImage ?? undefined,
    }
    if (_option) {
      options.push(_option)
    }
  }

  return options.reduce((uniques: TypeOption[], option) => {
    return [...uniques.filter((o) => o.id !== option.id), option]
  }, [])
}

export const getAcceptedTypeOptions = ({
  refBlock,
  blockTreeManager,
}: {
  refBlock: ReferenceBlock
  blockTreeManager: AtLeast<
    TreeManager<Block, CommonBlocksCompoundIndexes>,
    'getNode'
  >
}): TypeOption[] => {
  return (
    refBlock.constraints.acceptedTypes?.map((compId) => {
      const compNode = blockTreeManager.getNode(compId, {
        fallbackKey: 'sourceBlockId',
      })
      if (!compNode || !isComponentBlock(compNode.value)) return null
      const id = idToSourceBlockId(compNode.value)
      invariant(
        id,
        `id should be defined by this point. evaluating ${JSON.stringify(
          compNode.value
        )}`
      )

      const parentBlock = compNode.parentKey
        ? blockTreeManager.getNode(compNode.parentKey)?.value
        : null

      return {
        id,
        label: getBlockDisplayData({
          block: compNode.value,
          parentBlock,
        }).displayTitle,
        type: compNode.value.type,
        preview: compNode.value.typeOptions.displayImage ?? undefined,
      }
    }) ?? []
  ).filter(Boolean)
}

export const findAcceptedTypeOptionForBlock = ({
  refBlock,
  evaluatedBlock,
  typeOptions,
}: {
  refBlock: ReferenceBlock | RichTextBlock
  evaluatedBlock: Block
  typeOptions: TypeOption[]
}) => {
  if (!isReferenceableBlock(evaluatedBlock)) {
    return null
  }

  const res =
    typeOptions.find((option) => {
      if (
        refBlock.type === 'rich-text'
          ? !refBlock.constraints.allowedInternalLinks?.includes(option.id)
          : !refBlock.constraints.acceptedTypes?.includes(option.id)
      ) {
        return false
      }
      if (isComponentBlock(evaluatedBlock)) {
        return (
          evaluatedBlock.id === option.id ||
          evaluatedBlock.sourceBlockId === option.id
        )
      }
      if (isInstanceBlock(evaluatedBlock)) {
        return evaluatedBlock.typeOptions.targetBlockId === option.id
      }
      return false
    }) ?? null

  return res
}

export type InstanceSearchQueueItem = {
  brId: string
  blockId: string
  title: string
  sourceBlockId: string
  componentIndex: number
}

export const compareInstanceSearchQueueItem = (
  a: AtLeast<
    InstanceSearchQueueItem,
    'componentIndex' | 'title' | 'sourceBlockId'
  >,
  b: AtLeast<
    InstanceSearchQueueItem,
    'componentIndex' | 'title' | 'sourceBlockId'
  >
) => {
  if (a.componentIndex !== b.componentIndex) {
    return a.componentIndex - b.componentIndex
  }
  if (a.title && b.title) {
    // Both have titles: sort by title
    return a.title.localeCompare(b.title)
  }
  if (a.title && !b.title) {
    // a has title, b doesn't: a comes first
    return -1
  }
  if (!a.title && b.title) {
    // b has title, a doesn't: b comes first
    return 1
  }
  // Neither has title: sort by ID
  return a.sourceBlockId.localeCompare(b.sourceBlockId)
}
